import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { useForm, usePage } from '@inertiajs/react'
import { useEffect, useState, useCallback } from 'react'
import type { SharedProps } from '@/types/inertia'
import { IconSelect } from '@/components/ui/IconSelect'
import { LIGHT_LEVEL_ICONS, HUMIDITY_LEVEL_ICONS } from '@/lib/icons'

interface LocationData {
  id: number | null
  name: string
  light_level: string | null
  humidity_level: string | null
  temperature_range: string | null
  has_heating_nearby: boolean
  notes: string | null
}

interface Props {
  location: LocationData
}

const lightLevelOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'bright_direct', label: 'Jasne bezpośrednie' },
  { value: 'bright_indirect', label: 'Jasne rozproszone' },
  { value: 'medium', label: 'Półcień' },
  { value: 'low', label: 'Cień' },
]

const humidityOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'high', label: 'Wysoka' },
  { value: 'medium_humidity', label: 'Średnia' },
  { value: 'low_humidity', label: 'Niska' },
]

function LocationsNew({ location }: Props) {
  const { errors } = usePage<SharedProps>().props
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  const { data, setData, post, processing } = useForm({
    location: {
      name: location.name || '',
      light_level: location.light_level || '',
      humidity_level: location.humidity_level || '',
      temperature_range: location.temperature_range || '',
      has_heating_nearby: location.has_heating_nearby ?? false,
      notes: location.notes || '',
      photo: null as File | null,
    },
  })

  const setField = useCallback((field: string, value: unknown) => {
    setData('location', { ...data.location, [field]: value })
  }, [data.location, setData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/locations', { forceFormData: true })
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setData('location', { ...data.location, photo: file })
    if (file) {
      setPhotoPreview(URL.createObjectURL(file))
    } else {
      setPhotoPreview(null)
    }
  }

  useEffect(() => {
    return () => { if (photoPreview) URL.revokeObjectURL(photoPreview) }
  }, [photoPreview])

  return (
    <AtlasPage>
      <PageTitle title="Nowe miejsce" subtitle="Dodaj lokalizację dla roślin" />
      <Separator />

      <form onSubmit={handleSubmit} className="space-y-md">
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <FormField label="Nazwa" error={errors?.name}>
            <input
              type="text"
              value={data.location.name}
              onChange={(e) => setField('name', e.target.value)}
              className="form-input"
              required
              placeholder="np. Parapet w salonie"
            />
          </FormField>

          <FormField label="Poziom światła">
            <IconSelect value={data.location.light_level} onChange={(v) => setField('light_level', v)} options={lightLevelOptions} iconMap={LIGHT_LEVEL_ICONS} />
          </FormField>

          <FormField label="Wilgotność">
            <IconSelect value={data.location.humidity_level} onChange={(v) => setField('humidity_level', v)} options={humidityOptions} iconMap={HUMIDITY_LEVEL_ICONS} />
          </FormField>

          <FormField label="Zakres temperatur">
            <input
              type="text"
              value={data.location.temperature_range}
              onChange={(e) => setField('temperature_range', e.target.value)}
              className="form-input"
              placeholder="np. 18-24°C"
            />
          </FormField>

          <FormField label="Ogrzewanie w pobliżu">
            <label className="inline-flex items-center gap-sm mt-xs">
              <input
                type="checkbox"
                checked={data.location.has_heating_nearby}
                onChange={(e) => setField('has_heating_nearby', e.target.checked)}
                className="h-4 w-4 border-frame text-botanical focus:ring-botanical"
              />
              <span className="font-body text-sm text-ink">Kaloryfer lub inne źródło ciepła</span>
            </label>
          </FormField>
        </div>

        <FormField label="Notatki">
          <textarea
            value={data.location.notes}
            onChange={(e) => setField('notes', e.target.value)}
            className="form-input min-h-[4rem]"
          />
        </FormField>

        <FormField label="Zdjęcie">
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="block w-full font-label text-sm text-frame file:mr-sm file:border file:border-frame file:bg-paper-dark file:px-sm file:py-xs file:text-xs file:text-ink file:font-label"
          />
          {photoPreview && (
            <img src={photoPreview} alt="" className="mt-sm h-24 w-24 object-cover border border-frame/30" />
          )}
        </FormField>

        <Separator />

        <div className="flex items-center justify-center gap-md">
          <Button type="submit" disabled={processing}>
            Dodaj miejsce
          </Button>
          <Button href="/locations" variant="secondary">
            Anuluj
          </Button>
        </div>
      </form>

      <PageNumber number={12} />
    </AtlasPage>
  )
}

function FormField({
  label,
  error,
  children,
}: {
  label: string
  error?: string[]
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">
        {label}
      </label>
      {children}
      {error && (
        <p className="text-xs text-danger mt-xs">{Array.isArray(error) ? error.join(', ') : error}</p>
      )}
    </div>
  )
}

LocationsNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default LocationsNew
